import { lockrGet, lockrSet } from '@/tools/lockr'

export const MULTIPART_CHECKPOINT_TTL = 24 * 60 * 60 * 1000
export const MULTIPART_CHECKPOINT_MAX_COUNT = 20
export const MULTIPART_CHECKPOINT_MAX_BYTES = 2 * 1024 * 1024
const STORAGE_KEY = 'oss-multipart-checkpoints-v1'

function getStorageId (fileKey, scopeKey) {
  return `${scopeKey || 'anonymous'}:${fileKey}`
}

function readCheckpoints () {
  const checkpoints = lockrGet(STORAGE_KEY, {})
  const normalized = checkpoints && typeof checkpoints === 'object' ? checkpoints : {}
  const pruned = pruneCheckpoints(normalized)
  if (Object.keys(pruned).length !== Object.keys(normalized).length) {
    writeCheckpoints(pruned)
  }
  return pruned
}

function pruneCheckpoints (checkpoints) {
  const now = Date.now()
  const validEntries = Object.entries(checkpoints).filter(([, checkpoint]) => (
    checkpoint &&
    typeof checkpoint.fileKey === 'string' &&
    typeof checkpoint.uploadId === 'string' &&
    Array.isArray(checkpoint.completedParts) &&
    ['uploading', 'completing'].includes(checkpoint.status) &&
    Number.isSafeInteger(checkpoint.createdAt) &&
    now - checkpoint.createdAt < MULTIPART_CHECKPOINT_TTL
  ))
  validEntries.sort((left, right) => right[1].createdAt - left[1].createdAt)
  const result = validEntries.slice(0, MULTIPART_CHECKPOINT_MAX_COUNT).reduce((result, [key, value]) => {
    result[key] = value
    return result
  }, {})
  const oldestKeys = Object.keys(result).reverse()
  while (oldestKeys.length && JSON.stringify(result).length > MULTIPART_CHECKPOINT_MAX_BYTES) {
    delete result[oldestKeys.shift()]
  }
  return result
}

function writeCheckpoints (checkpoints) {
  try {
    lockrSet(STORAGE_KEY, checkpoints)
  } catch (error) {
    // 普通缓存不可用时不阻断当前上传，只会失去跨页面恢复能力。
    if (typeof window !== 'undefined' && window.__bl) {
      window.__bl.error({
        name: 'OSS分片断点缓存写入失败',
        message: error && error.message ? error.message : String(error)
      }, {
        filename: 'tools/oss-multipart/checkpoint.js',
        lineno: 39,
        colno: 0
      })
    }
  }
}

export function getFileIdentity (file) {
  return {
    name: file.name || '',
    size: file.size,
    lastModified: file.lastModified || 0
  }
}

export function getCheckpoint (fileKey, file, partSize, partCount, scopeKey) {
  const checkpoints = readCheckpoints()
  const storageId = getStorageId(fileKey, scopeKey)
  const checkpoint = checkpoints[storageId]
  if (!checkpoint) {
    return null
  }

  const identity = getFileIdentity(file)
  const completedPartsValid = Array.isArray(checkpoint.completedParts) &&
    new Set(checkpoint.completedParts.map(part => part.number)).size === checkpoint.completedParts.length &&
    checkpoint.completedParts.every(part => (
      Number.isInteger(part.number) &&
      part.number >= 1 &&
      part.number <= partCount &&
      typeof part.etag === 'string' &&
      Number.isSafeInteger(part.size) &&
      part.size > 0
    ))
  const valid = checkpoint.fileKey === fileKey &&
    checkpoint.scopeKey === scopeKey &&
    checkpoint.fileName === identity.name &&
    checkpoint.fileSize === identity.size &&
    checkpoint.fileLastModified === identity.lastModified &&
    checkpoint.partSize === partSize &&
    checkpoint.partCount === partCount &&
    typeof checkpoint.uploadId === 'string' &&
    checkpoint.uploadId.length > 0 &&
    completedPartsValid &&
    ['uploading', 'completing'].includes(checkpoint.status) &&
    (checkpoint.status !== 'completing' || checkpoint.completedParts.length === partCount) &&
    Date.now() - checkpoint.createdAt < MULTIPART_CHECKPOINT_TTL

  if (valid) {
    return checkpoint
  }
  delete checkpoints[storageId]
  writeCheckpoints(checkpoints)
  return null
}

export function saveCheckpoint (checkpoint) {
  const checkpoints = readCheckpoints()
  checkpoints[getStorageId(checkpoint.fileKey, checkpoint.scopeKey)] = checkpoint
  writeCheckpoints(pruneCheckpoints(checkpoints))
}

export function removeCheckpoint (fileKey, scopeKey) {
  const checkpoints = readCheckpoints()
  delete checkpoints[getStorageId(fileKey, scopeKey)]
  writeCheckpoints(checkpoints)
}

export function createCheckpoint ({ fileKey, file, uploadId, partSize, partCount, scopeKey }) {
  const identity = getFileIdentity(file)
  return {
    fileKey,
    scopeKey,
    fileName: identity.name,
    fileSize: identity.size,
    fileLastModified: identity.lastModified,
    uploadId,
    partSize,
    partCount,
    completedParts: [],
    createdAt: Date.now(),
    status: 'uploading'
  }
}
